import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../hooks/useAuth';
import CreditsDisplay from '../CreditsDisplay/CreditsDisplay';
import ProfileModal from '../ProfileModal/ProfileModal';
import './UserProfile.css';

// Check if we're on mobile for portal rendering
const isMobile = () => typeof window !== 'undefined' && window.innerWidth <= 640;

export default function UserProfile({
  onShowAuth,
  showMyMemories,
  onToggleMyMemories,
  onCreateVideo,
  credits,
  creditsLoading,
  onOpenCredits,
  onShowInbox,
  inboxUnreadCount = 0,
}) {
  const { user, signOut } = useAuth();
  const [showMenu, setShowMenu] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);

  const handleSignOut = async () => {
    setSigningOut(true);
    await signOut();
    setSigningOut(false);
    setShowMenu(false);
  };

  const handleToggleMyMemories = () => {
    if (onToggleMyMemories) {
      onToggleMyMemories();
    }
    setShowMenu(false);
  };

  const handleCreateVideo = () => {
    if (onCreateVideo) {
      onCreateVideo();
    }
    setShowMenu(false);
  };

  if (!user) {
    return (
      <button
        className="user-profile-button user-profile-guest"
        onClick={onShowAuth}
        aria-label="Sign in"
      >
        <svg className="user-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
        <span className="user-profile-text">Sign in</span>
      </button>
    );
  }

  const displayName = user.displayName || user.email?.split('@')[0] || 'User';
  const photoURL = user.photoURL;

  return (
    <div className="user-profile-container">
      {/* Credits display button */}
      <CreditsDisplay
        credits={credits}
        loading={creditsLoading}
        onClick={onOpenCredits}
      />
      <button
        className={`user-profile-button ${showMyMemories ? 'filter-active' : ''}`}
        onClick={() => setShowMenu(!showMenu)}
        aria-label="User menu"
      >
        {photoURL ? (
          <img 
            src={photoURL} 
            alt={displayName}
            className="user-profile-avatar"
          />
        ) : (
          <div className="user-profile-avatar user-profile-avatar-placeholder">
            {displayName.charAt(0).toUpperCase()}
          </div>
        )}
        <span className="user-profile-text user-profile-name">{displayName}</span>
        {showMyMemories && (
          <span className="filter-badge" title="Showing only your memories">
            📍
          </span>
        )}
        <svg 
          className={`user-profile-chevron ${showMenu ? 'open' : ''}`}
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {showMenu && (() => {
        const menuContent = (
          <>
            <div
              className="user-profile-backdrop"
              onClick={() => setShowMenu(false)}
            />
            <div className="user-profile-menu">
              <div className="user-profile-menu-handle" aria-hidden="true" />
              <div className="user-profile-menu-header">
                <div className="user-profile-menu-info">
                  {photoURL ? (
                    <img
                      src={photoURL}
                      alt={displayName}
                      className="user-profile-menu-avatar"
                    />
                  ) : (
                    <div className="user-profile-menu-avatar user-profile-menu-avatar-placeholder">
                      {displayName.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div>
                    <div className="user-profile-menu-name">{displayName}</div>
                    <div className="user-profile-menu-email">{user.email}</div>
                  </div>
                </div>
              </div>
              <div className="user-profile-menu-divider"></div>
              <button
                className="user-profile-menu-item profile-menu-item"
                onClick={() => {
                  setShowProfileModal(true);
                  setShowMenu(false);
                }}
              >
                <span className="menu-item-icon">🐧</span>

                Profile
              </button>
              <button
                className="user-profile-menu-item inbox-menu-item"
                onClick={() => {
                  if (onShowInbox) onShowInbox();
                  setShowMenu(false);
                }}
              >
                <span className="menu-item-icon">💌</span>
                Letters
                <span className="menu-item-beta">BETA</span>
                {inboxUnreadCount > 0 && (
                  <span className="menu-item-unread-badge">{inboxUnreadCount}</span>
                )}
              </button>
              <button
                className={`user-profile-menu-item ${showMyMemories ? 'active' : ''}`}
                onClick={handleToggleMyMemories}
              >
                <span className="menu-item-icon">📸</span>
                {showMyMemories ? 'Show All Memories' : 'My Memories Only'}
              </button>
              <button
                className="user-profile-menu-item video-menu-item"
                onClick={handleCreateVideo}
              >
                <span className="menu-item-icon">🎥</span>
                Create Video
                <span className="menu-item-beta">BETA</span>
                <span className="menu-item-badge">✨</span>
              </button>
              <button
                className="user-profile-menu-item credits-menu-item"
                onClick={() => {
                  if (onOpenCredits) onOpenCredits();
                  setShowMenu(false);
                }}
              >
                <span className="menu-item-icon gem-icon">💎</span>
                Buy Gems
                <span className="menu-item-credits">{credits ?? '-'}</span>
              </button>
              <div className="user-profile-menu-divider"></div>
              <button
                className="user-profile-menu-item"
                onClick={handleSignOut}
                disabled={signingOut}
              >
                {signingOut ? (
                  <>
                    <span className="spinner-small"></span>
                    Signing out...
                  </>
                ) : (
                  <>
                    <svg className="menu-item-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    Sign out
                  </>
                )}
              </button>
            </div>
          </>
        );

        // Use portal on mobile to escape the transformed parent container
        return isMobile()
          ? createPortal(menuContent, document.body)
          : menuContent;
      })()}

      {/* Profile Modal */}
      <ProfileModal
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        user={user}
      />
    </div>
  );
}

